// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useState, useCallback, useMemo, isValidElement } from 'react';

import { Flex, Checkbox, TextInput, Divider, Box, type TextProps, Stack, type Primitive } from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';

import { TextWithTruncate } from 'components/Mantine/TextWithTruncate';
import type { WrappedCheckListProps } from 'components/Mantine/TypeSafeSelect/types';
import { isNotNil } from 'helpers/isNotNil';
import { TriggerOnVisible } from 'hooks/useOnScreen';

const RenderOption = <T extends Primitive, D extends object>({
  option,
  disabled,
  hideCheckboxes,
  selectOption,
  selected,
  textProps,
}: {
  disabled: boolean | undefined;
  hideCheckboxes: boolean | undefined;
  option: WrappedCheckListProps<T, D>['data'][number];
  selectOption: (opt: T) => void;
  selected: boolean;
  textProps?: TextProps;
}) => (
  <Flex key={String(option.value)} align="center" gap="sm">
    {!hideCheckboxes && <Checkbox onChange={() => selectOption(option.value)} checked={selected} disabled={disabled} />}
    <Box
      w="100%"
      fz="sm"
      onClick={disabled ? undefined : () => selectOption(option.value)}
      style={{ cursor: disabled ? undefined : 'pointer', lineHeight: 1.5, overflow: 'hidden' }}
    >
      {isValidElement(option.label) ? (
        option.label
      ) : (
        <TextWithTruncate {...textProps}>{option.label ?? option.value}</TextWithTruncate>
      )}
    </Box>
  </Flex>
);

export const WrappedCheckList = <T extends Primitive, D extends object = object>({
  defaultValue: parentDefaultValue,
  value: parentValue,
  onChange: parentOnChange,
  data,
  entireCount = data.length,
  onOptionSubmit,
  hideCheckboxes,
  selectAllState,
  textInputProps,
  flexListProps,
  rightSideComponent,
  searchDisabled,
  searchable = true,
  filterInclude,
  nothingFoundMessage = 'No results found.',
  onScrollToEnd,
  loading,
  disableOption,
  disabled,
  searchValue,
  pendingSearchValue,
  onSearchChange,
}: WrappedCheckListProps<T, D>) => {
  const [selectAll, setSelectAll] = selectAllState ?? [];

  const [searchPhrase, setSearchPhrase] = useUncontrolled({
    value: pendingSearchValue ?? searchValue,
    defaultValue: '',
    onChange: onSearchChange,
  });

  const [selectedOptions, setSelectedOptions] = useUncontrolled<T[]>({
    value: parentValue,
    defaultValue: parentDefaultValue,
    onChange: parentOnChange,
  });

  const [pinnedSelectedOptions, setPinnedSelectedOptions] = useState(selectedOptions);

  const selectOption = useCallback(
    (option: T) => {
      const newOptions = [...selectedOptions];
      const indexOf = newOptions.indexOf(option);
      if (indexOf >= 0) {
        newOptions.splice(indexOf, 1);
      } else {
        newOptions.push(option);
      }
      setSelectedOptions(newOptions);
      onOptionSubmit?.(option);
    },
    [onOptionSubmit, selectedOptions, setSelectedOptions],
  );

  useEffect(() => {
    if (loading) {
      setPinnedSelectedOptions(selectedOptions);
    }
  }, [loading, selectedOptions]);

  const pinnedOptions = useMemo(
    () => data.filter((o) => pinnedSelectedOptions.includes(o.value)),
    [data, pinnedSelectedOptions],
  );
  const remainingOptions = useMemo(
    () =>
      data.filter(
        (o) => !pinnedSelectedOptions.some((so) => so === o.value) && (filterInclude?.(o, searchPhrase) ?? true),
      ),
    [data, filterInclude, pinnedSelectedOptions, searchPhrase],
  );

  const hasSearchResults = (searchValue ?? searchPhrase).length > 0;

  const renderOption = (o: (typeof data)[number]) => (
    <RenderOption<T, D>
      key={String(o.value)}
      option={o}
      selectOption={selectOption}
      selected={selectAll || selectedOptions.some((s) => s === o.value)}
      disabled={selectAll || o.disabled || disableOption?.(o.value) || disabled}
      hideCheckboxes={hideCheckboxes}
    />
  );

  return (
    <Stack gap="md" style={{ overflow: 'hidden' }}>
      {(searchable || isNotNil(rightSideComponent)) && (
        <Flex gap="sm" align="center" justify="space-between" flex="0 1 auto">
          {searchable && (
            <TextInput
              placeholder="Search..."
              value={searchPhrase}
              onChange={(e) => setSearchPhrase(e.target.value)}
              disabled={selectAll || searchDisabled}
              flex={1}
              {...textInputProps}
            />
          )}
          {rightSideComponent}
        </Flex>
      )}
      <Flex
        direction="column"
        gap="xs"
        {...flexListProps}
        style={{
          overflowX: 'hidden',
          overflowY: 'auto',
          // TODO: `.style` can now be a function, need to handle it
          // oxlint-disable-next-line typescript/no-misused-spread
          ...flexListProps?.style,
        }}
      >
        {pinnedOptions.map(renderOption)}
        {(hasSearchResults || pinnedOptions.length > 0) && (
          <Divider c="dimmed" label={hasSearchResults ? 'Search results' : undefined} />
        )}
        {!hasSearchResults && !hideCheckboxes && entireCount > 1 && isNotNil(setSelectAll) && !loading && (
          <RenderOption
            option={{ value: '(Select all)' }}
            selectOption={() => {
              setSelectedOptions([]);
              setSelectAll((v) => !v);
            }}
            selected={selectAll ?? false}
            disabled={!!pendingSearchValue}
            hideCheckboxes={false}
            textProps={{ opacity: 0.6 }}
          />
        )}
        {remainingOptions.map(renderOption)}

        {!loading && entireCount === 0 && (
          <Box c="dimmed" fz="sm">
            {hasSearchResults ? nothingFoundMessage : 'No results found.'}
          </Box>
        )}
        {!!onScrollToEnd && <TriggerOnVisible onVisible={onScrollToEnd} loading={loading} />}
      </Flex>
    </Stack>
  );
};
